import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputMask, InputMaskModule } from 'primeng/inputmask';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-time-input',
  standalone: true,
  imports: [
    CommonModule, InputMaskModule, ReactiveFormsModule
  ],
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TimeInputComponent implements OnInit, OnDestroy {
  @ViewChild('timeInput') timeInput: InputMask | null = null;

  @Input() FC?: FormControl<string>;
  @Input() floatLabel: string | null = null;
  @Input() styleClass: string = 'text-center';

  subs: Subscription[] = [];

  ngOnInit(): void {
    this.subs.push(
      this.FC?.valueChanges.subscribe((x) => {
        // keep this here, otherwise the input will not be updated if the value is changed from elsewhere
        this.FC?.setValue(x, { emitEvent: false });
      }) ?? Subscription.EMPTY
    )
  }

  onTimeKeyDown(event: KeyboardEvent) {
    const timeInput: string | null | undefined = this.FC?.value;
    const inputEl = this.timeInput?.inputViewChild?.nativeElement as HTMLInputElement | undefined;
    if (timeInput) {
      switch (event.key) {
        case ':':
          if (timeInput.includes('H') && !timeInput.includes('HH')) {
            const x = timeInput.replace(`${timeInput[0]}H`, `0${timeInput[0]}`);
            // console.log(x);
            this.FC?.setValue(x, { emitEvent: false });
            inputEl?.setSelectionRange(3, 3);
          }
          break;
        default:
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}

export function timeValidator(control: AbstractControl) {

  const value = control.value as string | null;
  return timeStringValueValidator(value);
}

export function timeStringValueValidator(value: string | null) {
  if (!value) {
    return null;
  }

  const [hoursString, minutesString] = value.split(':');

  if (hoursString.includes('H') || minutesString.includes('M')) {
    return { invalid: true }
  }

  if (hoursString[0] !== 'H' && +hoursString[0] > 2) {
    return { invalid: true };
  }
  if (!hoursString.includes('H') && +hoursString >= 24) {
    return { invalid: true };
  }

  if (minutesString[0] !== 'M' && +minutesString[0] > 6) {
    return { invalid: true };
  }
  if (!minutesString.includes('M') && +minutesString > 60) {
    return { invalid: true };
  }

  return null;
}

export function timeStringToDate(date: Date, value: string | null, UTC: boolean = false) {
  if (!value) {
    return null;
  }

  const [hoursString, minutesString] = value.split(':');
  const [minutes, hours] = [+minutesString, +hoursString];
  // if minues or hours are nan
  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  if (UTC) {
    const newDate = new Date(date).toUTCLikeDate();
    newDate.setUTCHours(hours, minutes, 0);
    return newDate;
  } else {
    const newDate = new Date(date);
    newDate.setHours(hours, minutes, 0);
    return newDate;
  }
}

export function dateToTimeString(date: Date | null, UTC: boolean = false) {
  if (!date) {
    return null;
  }

  let hours = date.getHours();
  let minutes = date.getMinutes();
  if (UTC) {
    hours = date.getUTCHours();
    minutes = date.getUTCMinutes();
  }

  return `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;
}